import React, { useState } from "react";
import styles from "../../Application.module.scss";
import HeadingWithLine from "../../../../components/HeadingWithLine/HeadingWithLine";
import ButtonIcon from "../../../../components/ButtonIcon/ButtonIcon";
import useCommonStore from "../../../../stores/common";
import { useMediaQuery } from "../../../../hooks/useMediaQuery";
import {
  formatCurrency,
  maskNumber,
} from "../../../../../components/helper/helper";
import CrossIconRed from "../../../../assets/svg/icons/common/CrossIconRed";
import CheckMarkGreen from "../../../../assets/svg/icons/common/CheckMarkGreen";
import CaretRight24 from "../../../../assets/svg/icons/common/CaretRight24";

function CreditCardProcessing({ appData }) {
  const expandAppDetails = useCommonStore((state) => state.expandDetails);
  const [expanded, setExpanded] = useState(false);
  const isMobile = useMediaQuery(768);

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  const renderYesNo = (value) => {
    if (value !== "" && value !== undefined && value !== null) {
      return value === "yes" ? (
        <>
          <CheckMarkGreen /> Yes
        </>
      ) : (
        <>
          <CrossIconRed /> No
        </>
      );
    } else {
      return "-";
    }
  };

  const renderTakingCards = (takingCards) => {
    if (!takingCards) return "-";

    try {
      let parsedCards = takingCards;

      // If it's a string, try to parse it as JSON
      if (typeof takingCards === "string") {
        try {
          parsedCards = JSON.parse(takingCards);
        } catch (e) {
          // If JSON parse fails, split by comma
          parsedCards = takingCards.split(",").map((item) => item.trim());
        }
      }

      // Handle both array and single value cases
      if (Array.isArray(parsedCards)) {
        return parsedCards.filter((card) => card).join(", ");
      } else if (parsedCards) {
        return parsedCards.toString();
      }

      return "-";
    } catch (error) {
      console.error("Error parsing taking cards", error);
      return "-";
    }
  };

  const renderAdditionalServices = () => {
    const servieArr = [
      appData?.creditcardprocess?.fraud_prevention === "yes"
        ? "Fraud & Chargeback tools"
        : "",
      appData?.creditcardprocess?.cheque_processing === "yes"
        ? "Accepting eCheck"
        : "",
      appData?.powerappbusinessprofile?.business_funding_required
        ? "Business Funding"
        : "",
    ];

    const result = servieArr.filter((item) => item && item.trim?.() !== "");

    if (result.length > 0) {
      return result.join(", ");
    }

    return "-";
  };

  const renderSection = (title, items) => {
    return (
      <div className={styles.info_section}>
        {title && (
          <>
            <div className={styles.section_title}>
              <h5>{title}</h5>
            </div>
            <div className={styles.desktop_heading}>
              <HeadingWithLine text={title} />
            </div>
          </>
        )}
        <div className={styles.info_grid}>
          {items.map((item, index) => (
            <div key={index} className={styles.info_item}>
              <span className={styles.label}>{item.label}</span>
              <span className={styles.value}>{item.value}</span>
            </div>
          ))}
        </div>
        <ul className={styles.desktop_list}>
          {items.map((item, index) => (
            <li key={index}>
              <div>
                <p>{item.label}</p>
                <p>{item.value}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const firstListItems = [
    { label: "Currently accept Visa, MasterCard, Discover and/or AMEX", value: renderYesNo(appData?.creditcardprocess?.accept_vmdax) },
    { label: "Ways of accepting credit & debit cards", value: renderTakingCards(appData?.creditcardprocess?.takingcards) },
  ];

  const thirdListItems = [
    { label: "Sales Swiped", value: `${appData?.powerappbusinessprofile?.origination_of_transaction?.sales_swiped || 0}%` },
    { label: "Sales Keyed-In", value: `${appData?.powerappbusinessprofile?.origination_of_transaction?.sales_keyed || 0}%` },
    { label: "Internet", value: `${appData?.creditcardprocess?.internet || appData?.powerappbusinessprofile?.origination_of_transaction?.internet || 0}%` },
    { label: "Recurring Billing", value: renderYesNo(appData?.powerappbusinessprofile?.recurring_bill_triel_radio) },
  ];

  const fourthListItems = [
    { label: "General Comments by Inspector", value: appData?.powerappbusinessprofile?.general_comments_by_inspector || "-" },
  ];

  const processingAmountsItems = [
    { label: "Max Monthly Sales", value: `$ ${appData?.bankinfo?.maxsales ? formatCurrency(appData?.bankinfo?.maxsales) : "-"}` },
    { label: "Average Sale Amount", value: `$ ${appData?.bankinfo?.ticketaverage ? formatCurrency(appData?.bankinfo?.ticketaverage) : "-"}` },
    { label: "Highest Sale Amount", value: `$ ${appData?.bankinfo?.tickethighest ? formatCurrency(appData?.bankinfo?.tickethighest) : "-"}` },
  ];

  const bankDetailsItems = [
    { label: "Routing Number", value: appData?.bankinfo?.bankrouting ? maskNumber(appData?.bankinfo?.bankrouting) : "-" },
    { label: "Account Number", value: appData?.bankinfo?.bankaccount ? maskNumber(appData?.bankinfo?.bankaccount) : "-" },
    { label: "Bank Name", value: appData?.bankinfo?.bankname || "-" },
  ];

  const additionalServicesItems = [
    { label: "Services that merchant interested in", value: renderAdditionalServices() },
  ];

  return (
    <div
      className={`${isMobile ? styles.mobile_business_info : styles.credit_card_processing} ${expandAppDetails ? styles.expanded : ""}`}
    >
      <div className={isMobile ? styles.business_info_header : styles.head} onClick={toggleExpanded}>
        {isMobile ? (
          <>
            <CaretRight24 className={`${styles.caret_icon} ${expanded ? styles.expanded : ""}`} />
            <h4>Credit Card Processing</h4>
          </>
        ) : (
          <>
            <ButtonIcon>
              <CaretRight24 className={expanded ? styles.expanded : ""} />
            </ButtonIcon>
            <h5>Credit Card Processing</h5>
          </>
        )}
      </div>

      <div
        className={`${isMobile ? styles.mobile_content : styles.body} ${expanded ? (isMobile ? styles.expanded : styles.body_expanded) : ""} ${expandAppDetails ? styles.app_details_expanded : ""}`}
      >
        {expanded && (
          <>
            <div className={styles.first_section}>
              {renderSection(null, firstListItems)}
            </div>
            <div className={styles.third_section}>
              {renderSection(null, thirdListItems)}
            </div>
            <div className={styles.fourth_section}>
              {renderSection(null, fourthListItems)}
            </div>
            {renderSection("Processing Amounts", processingAmountsItems)}
            <div className={styles.sixth_section}>
              {renderSection("Business Bank Details", bankDetailsItems)}
            </div>
            {renderSection("Additional services", additionalServicesItems)}
          </>
        )}
      </div>
    </div>
  );
}

export default CreditCardProcessing;
